import type {
  ActivityActivityCategory,
  GetBookingSummaryResponse,
  PlacePoi,
  PoiSummary
} from '@linktivity/tata';
import type { TFunction } from 'i18next';
import poiAttraction from '~/assets/poi-attraction.svg';
import poiExperience from '~/assets/poi-experience.svg';
import poiGourmet from '~/assets/poi-gourmet.svg';
import poiNature from '~/assets/poi-nature.svg';
import poiPurchased from '~/assets/poi-purchased.svg';
import poiShopping from '~/assets/poi-shopping.svg';
import poiTicket from '~/assets/poi-ticket.svg';
import { type POI, PoiDataType } from '~/layouts/Base/types';

export type PoiKey =
  | 'experience'
  | 'nature'
  | 'attraction'
  | 'gourmet'
  | 'shopping';

export const POI_COLORS: Record<PoiKey, string> = {
  experience: 'var(--color-category-experience)',
  nature: 'var(--color-category-nature)',
  attraction: 'var(--color-category-attraction)',
  gourmet: 'var(--color-category-gourmet)',
  shopping: 'var(--color-category-shopping)'
};

export const POI_ICONS: Record<PoiKey, string> = {
  attraction: poiAttraction,
  experience: poiExperience,
  gourmet: poiGourmet,
  nature: poiNature,
  shopping: poiShopping
};

export const POI_TICKET_ICON = poiTicket;
export const POI_PURCHASED_ICON = poiPurchased;
export const POI_TICKET_COLOR = 'var(--color-category-ticket)';

// A POI selling tickets that the user hasn't purchased (the purchased one is
// the main POI, built from the booking itself).
export function poiHasTicket(poi: POI): boolean {
  return (
    poi.data.type === PoiDataType.PoiSummary &&
    (poi.data.poiSummary?.products?.length ?? 0) > 0
  );
}

const CATEGORY_TO_POI_KEY: Record<string, PoiKey> = {
  ACTIVITY_CATEGORY_MARINE_SPORTS: 'experience',
  ACTIVITY_CATEGORY_OUTDOOR_ACTIVITIES: 'experience',
  ACTIVITY_CATEGORY_ONSEN: 'experience',
  ACTIVITY_CATEGORY_SPA_MASSAGE: 'experience',
  ACTIVITY_CATEGORY_JAPANESE_CULTURAL_EXPERIENCE: 'experience',
  ACTIVITY_CATEGORY_JAPANESE_CRAFT_EXPERIENCE: 'experience',
  ACTIVITY_CATEGORY_JAPANESE_COOKING_EXPERIENCE: 'experience',
  ACTIVITY_CATEGORY_OTHER_EXPERIENCE: 'experience',
  ACTIVITY_CATEGORY_ATTRACTION: 'attraction',
  ACTIVITY_CATEGORY_THEME_PARK: 'nature',
  ACTIVITY_CATEGORY_MUSEUM_GALLERY: 'attraction',
  ACTIVITY_CATEGORY_AQUARIUM_ZOO: 'attraction',
  ACTIVITY_CATEGORY_TEMPLES_SHINES: 'attraction',
  ACTIVITY_CATEGORY_TOWER_BUILDING: 'attraction',
  ACTIVITY_CATEGORY_SIGHTSEEING_TOUR: 'attraction',
  ACTIVITY_CATEGORY_JAPANESE_RESTAURANT: 'gourmet',
  ACTIVITY_CATEGORY_WESTERN_RESTAURANT: 'gourmet',
  ACTIVITY_CATEGORY_CHINESE_RESTAURANT: 'gourmet',
  ACTIVITY_CATEGORY_OTHER_RESTAURANT: 'gourmet',
  ACTIVITY_CATEGORY_DESSERT_BEVERAGE: 'gourmet'
};

// Delimiter for the synthetic place ids below. Google place ids only contain
// [A-Za-z0-9_-], so the real id can always be recovered with toGooglePlaceId.
const PLACE_ID_INDEX_DELIMITER = '#';

export function toGooglePlaceId(id: string | undefined): string | undefined {
  return id?.split(PLACE_ID_INDEX_DELIMITER)[0];
}

// Nearby POIs arrive grouped by place: each PlacePoi carries the place id
// plus one or more generated summaries of that place, whose own `place` field
// may be null. Keep every summary; since downstream code keys on place.id,
// summaries without their own place get a unique id derived from the group's
// id and their index (stable across polls, which append to the group).
export function placePoisToPoiSummaries(
  placePois: PlacePoi[] | undefined
): PoiSummary[] {
  return (placePois ?? []).flatMap(({ placeId, pois }) =>
    (pois ?? []).flatMap((poi, index) => {
      if (poi.place?.id) return [poi];
      if (!placeId) return [];
      const id =
        index === 0 ? placeId : `${placeId}${PLACE_ID_INDEX_DELIMITER}${index}`;
      return [{ ...poi, place: { ...poi.place, id } }];
    })
  );
}

export function categoryToPoiKey(
  category: ActivityActivityCategory | string
): PoiKey | null {
  return CATEGORY_TO_POI_KEY[category] ?? null;
}

export function formatRatingCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

export function formatPoiPrice(
  poi: PoiSummary | null | undefined,
  t: TFunction
): string | null {
  if (!poi) return null;
  const amount = poi.amount ? Number(poi.amount) : NaN;
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const currency = poi.products?.[0]?.currency ?? t('common.currencyJpy');
  return t('common.price', { currency, amount: amount.toLocaleString() });
}

export function bookingSummaryToPoi(
  booking: GetBookingSummaryResponse | null
): POI | null {
  const activitySummary = booking?.activitySummary;
  const poiSummary = booking?.poiSummaries?.[0];
  const place = activitySummary?.places?.[0];
  const location = place?.location;
  const bookingSummary = booking?.bookingSummary;

  // The POI id must be a place id (it's shared via the `poiId` URL param and
  // matched against nearby-POI place ids), never the booking id.
  const id = poiSummary?.place?.id ?? place?.id;
  if (!activitySummary || !location || !bookingSummary || !id) return null;
  return {
    id,
    name: poiSummary?.title ?? '',
    description: activitySummary.description ?? '',
    category: poiSummary?.category?.[0] ?? '',
    rating: poiSummary?.googleRating ? Number(poiSummary.googleRating) : 0,
    tags: [],
    image: poiSummary?.imageUrls?.[0],
    lng: location.longitude ?? 0,
    lat: location.latitude ?? 0,
    data: {
      type: PoiDataType.BookingSummary,
      bookingSummary: bookingSummary,
      poiSummary: poiSummary
    }
  };
}

export function poiSummaryToPoi(poiSummary: PoiSummary): POI | null {
  const id = poiSummary.place?.id;
  if (!id) return null;
  const location = poiSummary.place?.location;
  return {
    id,
    name: poiSummary.title ?? '',
    description: poiSummary.description ?? '',
    category: poiSummary.category?.[0] ?? '',
    rating: poiSummary.googleRating ? Number(poiSummary.googleRating) : 0,
    tags: [],
    image: poiSummary.imageUrls?.[0],
    lng: location?.longitude,
    lat: location?.latitude,
    data: {
      type: PoiDataType.PoiSummary,
      poiSummary: poiSummary
    }
  };
}
